import React from 'react';
import {
  ArrowPathIcon,
  BookOpenIcon,
  QueueListIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { useCatalogSync, useCatalogAssets } from '../../hooks/useCatalogRegistry';
import { usePendingCatalogCorrections } from '../../hooks/useCatalogApplet';
import type { CatalogCorrection } from '../../hooks/useCatalogApplet';
import { Badge } from '../ui';

export interface CatalogConnectionPanelsProps {
  connectorDisplayName: string;
  configId?: number;
}

function correctionStatusBadge(state: string) {
  switch (state) {
    case 'pending':
      return <Badge variant="warning">Needs steward</Badge>;
    case 'writeback_failed':
      return <Badge variant="danger">Writeback retry</Badge>;
    default:
      return <Badge variant="secondary">{state}</Badge>;
  }
}

function correctionTitle(row: CatalogCorrection): string {
  const assetName = row.catalog_asset?.display_name || row.catalog_asset?.name || `Asset ${row.catalog_asset_id}`;
  return `${row.field_path} correction for ${assetName}`;
}

export function CatalogConnectorOverviewSection({ connectorDisplayName, configId }: CatalogConnectionPanelsProps) {
  const syncMutation = useCatalogSync();
  const { data: assets, isLoading } = useCatalogAssets(configId);

  const handleSync = () => {
    if (configId) {
      syncMutation.mutate(configId);
    }
  };

  const assetCount = assets?.length || 0;

  return (
    <section className="space-y-4" data-testid="catalog-connector-overview">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
            <ShieldCheckIcon className="h-3.5 w-3.5" />
            Tenant catalog index (C1)
          </div>
          <h3 className="mt-1 text-sm font-medium text-charcoal dark:text-gray-100">Atlan-derived context workspace</h3>
          <p className="mt-1 max-w-3xl text-xs leading-5 text-gray-500 dark:text-gray-400">
            Read sync keeps glossary, stewardship, and lineage aligned with warehouse registry assets. Writeback is a
            governed adapter path with idempotency keys—corrections never bypass this queue.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleSync}
            disabled={syncMutation.isPending || !configId}
            className="flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-charcoal transition hover:bg-gray-50 disabled:opacity-50 dark:border-dark-border dark:text-gray-100 dark:hover:bg-dark-surface-2"
          >
            <ArrowPathIcon className={`h-3.5 w-3.5 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
            {syncMutation.isPending ? 'Syncing...' : 'Sync Catalog'}
          </button>
          <Badge variant="secondary">{connectorDisplayName}</Badge>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <ArrowPathIcon className="h-4 w-4 text-eliza-red" />
            Last successful sync
          </div>
          <p className="mt-2 text-sm font-semibold text-charcoal dark:text-gray-100">
            {syncMutation.isSuccess ? 'Just now' : 'Synced'}
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
            <BookOpenIcon className="h-4 w-4 text-eliza-red" />
            Indexed assets
          </div>
          <p className="mt-2 text-sm font-semibold text-charcoal dark:text-gray-100">
            {isLoading ? 'Loading...' : `${assetCount} objects`}
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
          <p className="text-xs text-gray-500 dark:text-gray-400">Resolver health</p>
          <p className="mt-2 text-sm font-semibold text-green-500">Auto-resolved</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface">
          <p className="text-xs text-gray-500 dark:text-gray-400">Writeback adapter</p>
          <p className="mt-2 text-sm font-semibold text-charcoal dark:text-gray-100">Enabled (descriptions)</p>
        </div>
      </div>
    </section>
  );
}

export function CatalogCorrectionQueueSection({ configId }: { configId?: number }) {
  const { data: corrections, isLoading, isError } = usePendingCatalogCorrections();
  const rows = (corrections || []).filter((row) =>
    configId ? row.catalog_asset?.connector_id === configId : true
  );

  return (
    <section className="space-y-4" data-testid="catalog-correction-queue">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500 dark:text-gray-400">
            <QueueListIcon className="h-3.5 w-3.5" />
            Correction requests (D5)
          </div>
          <h3 className="mt-1 text-sm font-medium text-charcoal dark:text-gray-100">Steward queue (deduped)</h3>
          <p className="mt-1 max-w-3xl text-xs leading-5 text-gray-500 dark:text-gray-400">
            Items aggregate evidence from workspace conversations and registry drill-ins. Approve, reject, convert to
            ontology draft, or retry writeback without mutating active runtime packs silently.
          </p>
        </div>
        <Badge variant="secondary">{isLoading ? 'Loading...' : `${rows.length} open`}</Badge>
      </div>

      <div className="space-y-3">
        {isError && (
          <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 text-xs text-amber-700 dark:text-amber-300">
            Unable to load pending catalog corrections.
          </div>
        )}
        {!isLoading && !isError && rows.length === 0 && (
          <div className="rounded-xl border border-gray-200 bg-white p-4 text-xs text-gray-500 dark:border-dark-border dark:bg-dark-surface dark:text-gray-400">
            No pending catalog corrections.
          </div>
        )}
        {rows.map((row) => (
          <div
            key={row.id}
            className="rounded-xl border border-gray-200 bg-white p-4 dark:border-dark-border dark:bg-dark-surface"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-charcoal dark:text-gray-100">{correctionTitle(row)}</p>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Field · {row.field_path}
                </p>
                {row.reason && <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">{row.reason}</p>}
              </div>
              <div className="flex flex-wrap items-center gap-2">{correctionStatusBadge(row.status)}</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
